/**
 * MailingAddressPanel - renders the "Mailing address" block on the
 * Property step of the registration wizard.
 *
 * The mailing address belongs to the customer/consumer entity. We show it on
 * the property step so the user can choose whether the customer's mailing
 * address mirrors the installation address while both addresses are in view.
 */

import { useMemo } from 'react'
import { Mail } from 'lucide-react'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import type { WizardState } from '@/admin/pages/registrations/new-registration-page'
import type { HostAttributeMeta } from '@/admin/types/hosts'
import { RegistrationPanel } from './layout'
import { MailingAddressCapture } from './mailing-address-capture'

interface Props {
  state: WizardState
  update: (payload: Partial<WizardState>) => void
  label: string
  required: boolean
  error?: string
  hostAttributes?: HostAttributeMeta[]
}

const ADDRESS_MAPS_TO_RESOLVED: Record<string, string> = {
  line_1: 'line_1',
  address_line_1: 'line_1',
  line_2: 'line_2',
  address_line_2: 'line_2',
  town_or_city: 'town_or_city',
  city: 'town_or_city',
  town: 'town_or_city',
  address_town_or_city: 'town_or_city',
  county: 'county',
  address_county: 'county',
  postcode: 'postcode',
  post_code: 'postcode',
  address_postcode: 'postcode',
  country: 'country',
  address_country: 'country',
}

function readAddressObject(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object') return {}
  const source = value as Record<string, unknown>
  return {
    line_1: String(source.line_1 ?? source.addressLine1 ?? ''),
    line_2: String(source.line_2 ?? source.addressLine2 ?? ''),
    town_or_city: String(source.town_or_city ?? source.locality ?? ''),
    county: String(source.county ?? source.region ?? ''),
    postcode: String(source.postcode ?? source.postalCode ?? ''),
    country: String(source.country ?? source.countryCode ?? ''),
  }
}

function formatAddress(parts: Record<string, string>) {
  return [
    parts.line_1,
    parts.line_2,
    parts.town_or_city,
    parts.county,
    parts.postcode,
    parts.country,
  ]
    .filter(Boolean)
    .join(', ')
}

export function MailingAddressPanel({
  state,
  update,
  label,
  required,
  error,
  hostAttributes = [],
}: Props) {
  const installationPreview = useMemo(() => {
    const h = state.hostAttributeValues
    const parts: Record<string, string> = {}

    for (const attr of hostAttributes) {
      const value = h[attr.code_name]
      if (value && typeof value === 'object') {
        Object.assign(parts, readAddressObject(value))
      }
      const key =
        ADDRESS_MAPS_TO_RESOLVED[attr.maps_to_column] ??
        ADDRESS_MAPS_TO_RESOLVED[attr.code_name]
      if (key && typeof value === 'string' && value.trim()) {
        parts[key] = value
      }
    }

    if (Object.keys(parts).length === 0) {
      Object.assign(parts, readAddressObject(h.address))
    }

    return formatAddress(parts)
  }, [hostAttributes, state.hostAttributeValues])

  return (
    <RegistrationPanel
      title={label}
      icon={<Mail size={14} className="text-[var(--ink-3)]" />}
    >
      <Label className="flex cursor-pointer items-center gap-2 text-[13px] font-light text-[var(--ink)]">
        <Checkbox
          checked={state.mailingSameAsInstallation}
          onCheckedChange={(v) =>
            update({
              mailingSameAsInstallation: v === true,
              ...(v === true ? { address: null } : {}),
            })
          }
          data-testid="mailing-same-as-installation"
        />
        Same as property address
      </Label>

      {state.mailingSameAsInstallation ? (
        installationPreview ? (
          <div className="mt-3 rounded-[6px] border-[0.5px] border-dashed border-[var(--rule-strong)] bg-[var(--bg-subtle)] px-3 py-2 text-[12px] text-[var(--ink-2)]">
            {installationPreview}
          </div>
        ) : null
      ) : (
        <div className="mt-3">
          {required && (
            <Label className="mb-2 block text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
              Mailing address
              <span className="ml-0.5 inline-block mt-2 text-[17px] leading-[4px] text-[var(--status-error)]">*</span>
            </Label>
          )}
          <MailingAddressCapture
            address={state.address}
            onChange={(next) => update({ address: next })}
            error={error}
            data-testid="mailing-address-capture"
          />
        </div>
      )}
    </RegistrationPanel>
  )
}
